import { Fragment, ReactNode } from 'react';

import { Cross2Icon } from '@radix-ui/react-icons';
import { Button, Dialog, Flex, IconButton, Text } from '@raystack/apsara';
import { Show } from '@studio/hoc';
import clsx from 'clsx';

import classes from './style.module.css';

type Btn = React.ComponentProps<typeof Button>;

interface ModalProps extends Btn {
  show?: boolean;
  onClose?: () => void;
  onEnter?: () => void;
  title?: string;
  subtitle?: string;
  buttonText?: string;
  footer?: ReactNode;
  children?: ReactNode;
  width?: number;
  classes?: {
    content?: string;
    header?: string;
    body?: string;
    footer?: string;
  };
}

export const Modal = ({
  show = false,
  width = 400,
  title = '',
  subtitle = '',
  footer = null,
  buttonText = '',
  color = 'accent',
  variant = 'solid',
  loaderText,
  loading: isLoading = false,
  disabled = false,
  classes: customClasses = {
    content: '',
    header: '',
    body: '',
    footer: ''
  },
  onClose = () => null,
  onClick = () => null,
  onEnter,
  children
}: ModalProps) => {
  return (
    <Dialog open={show}>
      <Dialog.Content
        overlayBlur
        width={width || 400}
        className={clsx(classes.defaultOverride, customClasses?.content)}
        onInteractOutside={onClose}
        onOpenAutoFocus={onEnter}
      >
        <Dialog.Header className={classes?.header}>
          <Dialog.Title>
            <Flex direction='column'>
              {title}
              <Text size={1}>{subtitle}</Text>
            </Flex>
          </Dialog.Title>
          <IconButton
            size={3}
            onClick={onClose}
            disabled={isLoading}
            data-test-id='modal-close-btn'
          >
            <Cross2Icon color='var(--rs-color-foreground-base-secondary)' />
          </IconButton>
        </Dialog.Header>
        <Dialog.Body className={clsx(classes.defaultBody, customClasses?.body)}>
          {children}
        </Dialog.Body>

        <Show
          when={!!footer}
          fallback={
            <Dialog.Footer className={customClasses?.footer}>
              <Button
                variant='outline'
                color='neutral'
                onClick={onClose}
                disabled={isLoading}
                data-test-id='modal-cancel-btn'
              >
                Cancel
              </Button>

              <Show when={buttonText?.length > 0}>
                <Button
                  color={color}
                  variant={variant}
                  disabled={disabled}
                  onClick={onClick}
                  data-test-id='modal-submit-btn'
                  loading={isLoading}
                  loaderText={loaderText || buttonText}
                >
                  {buttonText}
                </Button>
              </Show>
            </Dialog.Footer>
          }
        >
          <Fragment>{footer}</Fragment>
        </Show>
      </Dialog.Content>
    </Dialog>
  );
};
